import axios from "axios";

export const MAX_DOCUMENT_BYTES = 100 * 1024 * 1024;
export const DOCUMENT_CHUNK_BYTES = 1024 * 1024;
export const DOCUMENT_SIZE_LABEL = "100 MB per file";

export const documentSizeError = (file) => {
  if (!file || file.size === 0) return "Empty files cannot be uploaded.";
  if (file.size > MAX_DOCUMENT_BYTES) return `Maximum file size is ${DOCUMENT_SIZE_LABEL}.`;
  return null;
};

// One transport for individual submissions and admin bulk uploads. Retries use
// the same upload ID/index so the server can safely return the stored receipt.
export const uploadDocumentChunks = async ({ file, url, token, metadata = {}, onProgress }) => {
  const validation = documentSizeError(file);
  if (validation) throw new Error(validation);
  const uploadId = crypto.randomUUID();
  const count = Math.ceil(file.size / DOCUMENT_CHUNK_BYTES);
  let result;
  onProgress?.(0);
  for (let index = 0; index < count; index++) {
    const start = index * DOCUMENT_CHUNK_BYTES;
    const end = Math.min(start + DOCUMENT_CHUNK_BYTES, file.size);
    const body = new FormData();
    Object.entries(metadata).forEach(([key, value]) => body.append(key, value));
    body.append("upload_id", uploadId);
    body.append("chunk_index", String(index));
    body.append("total_chunks", String(count));
    body.append("file_size", String(file.size));
    body.append("file_name", file.name);
    body.append("chunk", file.slice(start, end, file.type || "application/octet-stream"), file.name);
    for (let attempt = 0; attempt < 4; attempt++) {
      try {
        result = await axios.post(url, body, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: index === count - 1 ? 300000 : 120000,
          onUploadProgress: (event) => {
            const sent = Math.min(event.loaded, end - start);
            onProgress?.(Math.min(99, Math.floor(((start + sent) / file.size) * 100)));
          },
        });
        break;
      } catch (error) {
        const status = error.response?.status;
        if (attempt === 3 || (status && status < 500 && status !== 408 && status !== 429)) throw error;
        await new Promise(resolve => setTimeout(resolve, 1500 * (attempt + 1)));
      }
    }
    onProgress?.(index === count - 1 ? 100 : Math.floor((end / file.size) * 100));
  }
  return result.data;
};